import { useState, useEffect, useRef } from 'react'
import { Building2, Search } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import api from '@/services/api'
import { formatDate } from '@/utils/format-date'
import type { Deal, EmpresaDetailed } from '@/types'
import { cnpjMask, onlyDigits } from './utils'
import { maskCnpjDisplay } from '@/utils/dealDisplay'
import { buildEmpresaPatchFromCnpjLookup } from '@/utils/empresaCnpjLookup'
import { InfoField } from './InfoField'

type CompanyData = Partial<EmpresaDetailed> & {
  situacao?: string
  naturezaJuridica?: string
  atividadePrincipal?: string
}

export function CompanyDataSection({
  deal,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
}) {
  const [cnpj, setCnpj] = useState('')
  const [searching, setSearching] = useState(false)
  const [companyData, setCompanyData] = useState<CompanyData>({})
  const [loadingCompany, setLoadingCompany] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const loadedOnce = useRef(false)

  const loadCompany = async (silent = false) => {
    if (!silent) setLoadingCompany(true)
    try {
      const { data } = await api.get(`/empresas/${deal.empresaId}`)
      const empresa = data.data ?? data
      if (empresa) {
        setCompanyData({
          cnpj: empresa.cnpj,
          razaoSocial: empresa.razaoSocial,
          nomeFantasia: empresa.nomeFantasia ?? empresa.nome,
          endereco: empresa.endereco,
          cep: empresa.cep,
          cidade: empresa.cidade,
          estado: empresa.estado,
          segmento: empresa.segmento,
          situacao: empresa.situacao,
          naturezaJuridica: empresa.naturezaJuridica,
        })
        const cnpjInput = document.getElementById('deal-company-cnpj')
        if (empresa.cnpj && document.activeElement !== cnpjInput) setCnpj(cnpjMask(empresa.cnpj))
      }
    } catch {
      // Company not found — silent
    } finally {
      if (!silent) setLoadingCompany(false)
    }
  }

  useEffect(() => {
    loadedOnce.current = false
    void loadCompany(false).then(() => {
      loadedOnce.current = true
    })
    function refreshIfVisible() {
      if (!loadedOnce.current || document.visibilityState !== 'visible') return
      void loadCompany(true)
    }
    document.addEventListener('visibilitychange', refreshIfVisible)
    window.addEventListener('focus', refreshIfVisible)
    const poll = window.setInterval(() => {
      if (loadedOnce.current) void loadCompany(true)
    }, 5000)
    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible)
      window.removeEventListener('focus', refreshIfVisible)
      window.clearInterval(poll)
    }
    // Recarrega ao trocar de empresa e quando a aba volta ao foco (cadastro público pode ter gravado).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.empresaId])

  const handleCnpjSearch = async () => {
    const rawCnpj = onlyDigits(cnpj)
    if (rawCnpj.length !== 14) {
      toast.error('CNPJ inválido — precisa ter 14 dígitos')
      return
    }
    setSearching(true)
    try {
      const { data } = await api.get(`/empresas/cnpj/${rawCnpj}?source=brasilapi`)
      const empresa = data.data ?? data
      if (empresa) {
        const patchPayload = buildEmpresaPatchFromCnpjLookup(empresa, rawCnpj, deal.empresaNome)
        const updated: CompanyData = {
          cnpj: patchPayload.cnpj,
          razaoSocial: patchPayload.razaoSocial,
          nomeFantasia: patchPayload.nomeFantasia,
          endereco: patchPayload.endereco,
          cep: patchPayload.cep,
          cidade: patchPayload.cidade,
          estado: patchPayload.estado,
          segmento: patchPayload.segmento,
          situacao: patchPayload.situacao,
          naturezaJuridica: patchPayload.naturezaJuridica,
          atividadePrincipal: patchPayload.atividadePrincipal,
        }
        setCompanyData(updated)
        setExpanded(true)
        await api.patch(`/empresas/${deal.empresaId}`, patchPayload)
        toast.success('Dados da empresa consultados e salvos automaticamente')
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { error?: { message?: string }; message?: string } } })
        ?.response?.status
      const apiMessage =
        (err as { response?: { data?: { error?: { message?: string }; message?: string } } })?.response?.data
          ?.error?.message ??
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      if (status === 404) {
        toast.error('CNPJ não encontrado')
      } else if (apiMessage) {
        toast.error(apiMessage)
      } else {
        toast.error('Erro ao buscar CNPJ')
      }
    } finally {
      setSearching(false)
    }
  }

  const hasCompanyDetails =
    companyData.razaoSocial ||
    companyData.nomeFantasia ||
    companyData.endereco ||
    companyData.cidade

  return (
    <div className="glass-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
          <Building2 size={14} />
          Dados da Empresa
        </h3>
        {hasCompanyDetails && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-accent hover:underline"
          >
            {expanded ? 'Recolher' : 'Ver mais'}
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <input
            id="deal-company-cnpj"
            value={cnpj}
            onChange={(e) => setCnpj(cnpjMask(e.target.value))}
            placeholder="00.000.000/0000-00"
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent font-mono tracking-wider"
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={<Search size={13} />}
          loading={searching}
          onClick={handleCnpjSearch}
        >
          Buscar
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <InfoField
          label="Empresa"
          value={companyData.nomeFantasia || companyData.razaoSocial || deal.empresaNome}
        />
        <InfoField label="CNPJ" value={companyData.cnpj ? maskCnpjDisplay(companyData.cnpj) : '—'} />
        <InfoField label="Serviço" value={deal.servico || '—'} />
        <InfoField label="Concorrente" value={deal.concorrente || '—'} />
        <InfoField
          label="Fechamento Previsto"
          value={deal.dataFechamentoPrevisto ? formatDate(deal.dataFechamentoPrevisto) : '—'}
        />
      </div>

      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="space-y-2 pt-2 border-t border-white/[0.06]"
        >
          {loadingCompany ? (
            <div className="flex justify-center py-3">
              <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {companyData.razaoSocial && (
                <InfoField label="Razão Social" value={companyData.razaoSocial} />
              )}
              {companyData.nomeFantasia && (
                <InfoField label="Nome Fantasia" value={companyData.nomeFantasia} />
              )}
              {companyData.cnpj && (
                <InfoField label="CNPJ" value={maskCnpjDisplay(companyData.cnpj)} />
              )}
              {companyData.segmento && (
                <InfoField label="Segmento" value={companyData.segmento} />
              )}
              {companyData.situacao && (
                <InfoField label="Situação" value={companyData.situacao} />
              )}
              {companyData.naturezaJuridica && (
                <InfoField label="Natureza Jurídica" value={companyData.naturezaJuridica} />
              )}
              {companyData.atividadePrincipal && (
                <InfoField label="Atividade Principal" value={companyData.atividadePrincipal} />
              )}
              {companyData.endereco && (
                <InfoField label="Endereço" value={companyData.endereco} />
              )}
              {companyData.cep && <InfoField label="CEP" value={companyData.cep} />}
              {companyData.cidade && (
                <InfoField
                  label="Cidade/Estado"
                  value={`${companyData.cidade}${companyData.estado ? ` / ${companyData.estado}` : ''}`}
                />
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  )
}
